import type { Styles } from '@adornis/ass/style.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { LocalStorageBehaviorController } from '@adornis/chemistry/controllers/local-storage-behavior-controller.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-card.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-grid.js';
import '@adornis/chemistry/elements/components/x-hr.js';
import '@adornis/chemistry/elements/components/x-link.js';
import '@adornis/forms/x-checkbox.js';
import '@adornis/popover/x-dropdown-selection.js';
import { routingState } from '@adornis/router/client/routing-state.js';
import { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { DocumentMeta, ensureDocumentMeta } from '@dla/db/DocumentMeta.js';
import type { User } from '@dla/db/User.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { of, switchMap } from 'rxjs';
import '../components/dla-collab-editor.js';
import '../components/dla-permission-editor.js';
import { DlaTranslationDict } from '../translations.js';

const ACCEPTED_LEGAL_KEY = 'accepted_legal';

@customElement('dla-writer')
export class DLAWriter extends ChemistryLitElement {
  @property() rid = new RXController(this, '');

  private _translator = new TranslationController<DlaTranslationDict>(this);

  @state() _minimalMode = new RXController(this, routingState.value.searchParams.get('minimal'));
  @state() _moodleMode = new RXController(this, routingState.value.searchParams.get('moodleMode'));
  @state() _debug = new RXController(this, routingState.value.searchParams.get('debug'));

  @state() _currentUser = new RXController(this, currentUser<User>());
  @state() _documentMeta = new RXController(
    this,
    this.rid.observable.pipe(
      switchMap(async rid => {
        if (!rid || rid === '') {
          return of(null);
        }

        try {
          await ensureDocumentMeta(rid, this._moodleMode.value === '1')();
        } catch (e: any) {}

        return DocumentMeta.subscribeByID<DocumentMeta>(rid)({
          ...DocumentMeta.allFields,
          permissions: {
            userID: 1,
            canRead: 1,
            canEdit: 1,
            user: {
              username: 1,
              name: 1,
            },
          },
        });
      }),
      switchMap(res => res),
    ),
  );

  private _accectedLegalLocalStorage = new LocalStorageBehaviorController(this, ACCEPTED_LEGAL_KEY, 'false');

  private _isOwner() {
    return this._documentMeta.value?.isOwner(this._currentUser.value);
  }

  private _isEditAllowed() {
    return this._documentMeta.value?.canEdit(this._currentUser.value);
  }

  private _isReadAllowed() {
    return this._documentMeta.value?.canRead(this._currentUser.value);
  }

  private _ownerView() {
    if (!this._isOwner() || this._minimalMode.value === '1' || this._moodleMode.value === '1') {
      return nothing;
    }

    return html`
      <x-h4> ${this._translator.translate('ownerSettings')} </x-h4>
      <x-hr></x-hr>
      <dla-permission-editor .documentMeta=${this._documentMeta.value}></dla-permission-editor>
    `;
  }

  override render() {
    if (this._documentMeta.isLoading || this._accectedLegalLocalStorage.isLoading) return html` <x-loader></x-loader> `;

    if (this._accectedLegalLocalStorage.value !== 'true') return this._acceptLegalContent();

    if (!this._documentMeta.value) {
      return;
    }

    if (!this._isReadAllowed()) {
      return html`
        <x-flex h100 padding="md" space="md">
          <x-text> ${this._translator.translate('accessDenied')} </x-text>
        </x-flex>
      `;
    }

    return html`
      <x-flex ${css({ height: '100%' })} space="md">
        <dla-collab-editor
          ${css({ height: '100%' })}
          .options=${{
            rid: this.rid.value,
            readonly: !this._isEditAllowed(),
            username: {
              username: this._currentUser.value?.name ?? this._currentUser.value?.username,
              userID: this._currentUser.value?._id,
            },
          }}
          ?show-debug-info=${this._debug.value === '1'}
        >
          <x-flex slot="afterComments" space="md"> ${this._ownerView()} </x-flex>
        </dla-collab-editor>
      </x-flex>
    `;
  }

  private _acceptCheckbox = new RXController(this, false);
  private _acceptLegalContent() {
    return html`
      <x-flex
        space="lg"
        ${css({
          width: '100%',
          margin: 'auto',
          maxWidth: this.sizes.maxReadableWidth,
          padding: `${this.spacing.md} 0`,
        })}
      >
        <x-h1> ${this._translator.translate('welcomeToOctipad')} </x-h1>
        <x-text>
          ${this._translator.translate('acceptLegal', 'pleaseRead')}
          <x-link href=${'/privacy'} ${css({ color: this.colors.accent })}>
            ${this._translator.translate('acceptLegal', 'privacyPolicy')} </x-link
          >.
        </x-text>
        <x-checkbox
          @click=${() => {
            this._acceptCheckbox.next(!this._acceptCheckbox.value);
          }}
          label=${this._translator.translate('acceptLegal', 'iAccept')}
        ></x-checkbox>
        <x-button
          ?disabled=${!this._acceptCheckbox.value}
          @click=${() => {
            this._accectedLegalLocalStorage.next('true');
          }}
          ${css({ marginRight: 'auto' })}
        >
          ${this._translator.translate('button', 'accept')}
        </x-button>
      </x-flex>
    `;
  }

  override styles(): Styles[] {
    return [
      ...super.styles(),
      {
        ':host': {
          height: '100%',
        },
      },
    ];
  }
}
